import { gql } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from '@chakra-ui/react';
import { t as translate } from 'i18next';
import { range } from 'lodash';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useCreateOrgAccessRequestMutation } from './__generated__/RequestAccessModal.graphql';

const DAY_OPTIONS = range(0, 31);
const HOUR_OPTIONS = range(0, 24);

interface Props {
  organization: { id: string; name: string } | null;
  onClose: () => void;
  onRequested: () => void;
}

export function RequestAccessModal({ organization, onClose, onRequested }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [days, setDays] = useState(1);
  const [hours, setHours] = useState(0);
  const [createRequest, { loading }] = useCreateOrgAccessRequestMutation();

  const durationMinutes = (days * 24 + hours) * 60;

  const handleSubmit = async () => {
    if (!organization || durationMinutes === 0) {
      return;
    }

    try {
      await createRequest({ variables: { orgId: organization.id, durationMinutes } });

      toast({
        status: 'success',
        title: (
          <Trans
            i18nKey="accessRequestSentSuccess"
            values={{ name: organization.name }}
            components={{ strong: <strong /> }}
          />
        ),
      });

      onRequested();
      onClose();
    } catch (err) {
      toast({
        status: 'error',
        title: t('accessRequestSendError'),
        description: fromError(err, 'CreateOrgAccessRequest', getRequestAccessErrorMap()),
      });
    }
  };

  return (
    <Modal isOpen={Boolean(organization)} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('requestAccessToOrganisation')}</ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          <chakra.p color="gray.600" marginBottom="6">
            {t('requestAccessDurationDescription')}
          </chakra.p>
          <FormControl isInvalid={durationMinutes === 0}>
            <HStack spacing="4" alignItems="flex-start">
              <FormControl>
                <FormLabel>{t('days')}</FormLabel>
                <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {DAY_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('hours')}</FormLabel>
                <Select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
                  {HOUR_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
            <FormErrorMessage>{t('requestAccessDurationRequired')}</FormErrorMessage>
          </FormControl>
          <Alert status="info" variant="left-accent" marginTop="6" fontSize="sm">
            <AlertIcon />
            <AlertDescription>{t('requestAccessAutoExpiryNotice')}</AlertDescription>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" marginRight="3" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={durationMinutes === 0}
          >
            {t('requestAccess')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function getRequestAccessErrorMap(): Record<string, string> {
  return {
    SERVICE_ADMIN_ORG_ACCESS_REQUEST_ALREADY_AWAITING: translate('accessRequestAlreadyAwaiting'),
    SERVICE_ADMIN_ORG_ACCESS_REQUEST_ALREADY_GRANTED: translate('accessRequestAlreadyGranted'),
    SERVICE_ADMIN_ORG_ACCESS_REQUEST_INELIGIBLE_TARGET_ORG: translate(
      'accessRequestIneligibleTargetOrg',
    ),
    SERVICE_ADMIN_ORG_ACCESS_REQUEST_INELIGIBLE_ORG: translate('accessRequestIneligibleOrg'),
  };
}

RequestAccessModal.graphql = {
  mutations: {
    CreateOrgAccessRequest: gql`
      mutation CreateOrgAccessRequest($orgId: ID!, $durationMinutes: Int!) {
        orgAccessRequestCreate(orgId: $orgId, durationMinutes: $durationMinutes) {
          id
          status
        }
      }
    `,
  },
};
